import { useMemo } from 'react';
import useSWR from 'swr';

import { UserRole, UserRoles, UserState, UserType, UserTypes } from '../user.types';

export const USER_INFO_API = '/api/user';
const fetcher = (url: string) => fetch(url).then((res) => res.json());

export type UseUserResponse = {
  data: UserState | undefined;
  error: Error | undefined;
  userType: UserType;
  userRole: UserRole;
  userId: string | undefined;
  email: string | undefined;
  fullName: string;
  isFreeUser: boolean;
  isPremiumUser: boolean;
  isEducationUser: boolean;
  isBusinessUser: boolean;
  isEnterpriseUser: boolean;
  isAdminUser: boolean;
  isTeamOwnerRole: boolean;
  isTeamMemberRole: boolean;
  isTeamAdminRole: boolean;
  isGuestRole: boolean;
};

export const useUser = (shouldFetch = true): UseUserResponse => {
  const { data, error } = useSWR<UserState>(shouldFetch ? USER_INFO_API : null, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });

  const userType = data?.user_type ?? UserTypes.Free;
  const userRole = data?.user_role ?? UserRoles.DEFAULT_ROLE;
  const fullName = `${data?.first_name} ${data?.last_name}`;
  const userId = data?.user_id;
  const email = data?.email;

  const result = useMemo(
    () => ({
      data,
      error,
      userType,
      userRole,
      userId,
      fullName,
      email,
      // User types
      isFreeUser: userType === UserTypes.Free,
      isEducationUser: userType === UserTypes.Education,
      isPremiumUser: userType === UserTypes.Premium,
      isBusinessUser: userType === UserTypes.Business,
      isEnterpriseUser: userType === UserTypes.Enterprise,
      isAdminUser: userType === UserTypes.Admin,
      //User roles
      isTeamOwnerRole: userRole === UserRoles.TEAM_OWNER,
      isTeamMemberRole: userRole === UserRoles.TEAM_MEMBER,
      isTeamAdminRole: userRole === UserRoles.ENTERPRISE_ADMIN,
      isGuestRole: userRole === UserRoles.GUEST,
    }),
    [data, error, userId, fullName, userType, userRole, email],
  );

  return result;
};
